/**
 * `<CandidatePhoto>` — tenant-aware photo wrapper over `<Avatar>` (foundations §9.14).
 *
 * Phase C3 (Batch 30f): refactored from the previous 3-state image-loader to
 * a thin wrapper. URL construction + `version` cache-buster live here; visual
 * rendering (image + initials fallback) delegates to `<Avatar>`. The previous
 * skeleton + fade-in behavior is intentionally dropped — Avatar shows initials
 * immediately when no src is loaded, and swaps to the image when it loads.
 *
 * Family contract (Batch 30b → 30c → 30d → 30e → 30f → P2-2.5):
 *   - sm → Avatar.sm   (32px, table rows)
 *   - md → Avatar.lg   (48px, Dashboard cards — canonical)
 *   - lg → Avatar.xl   (72px, Candidate page banner — was 80px pre-30f, shrunk per HoD Q6)
 *   - xl → Avatar.2xl  (96px, Candidate-page sidebar hero — P2-2.5-sidebar-avatar-primitive)
 *
 * A30e-3 (Batch 30e): additive ``version?: number`` prop. Used to force a
 * re-fetch of the photo when a candidate's ``linkedin_url`` changes
 * (server-side cache also invalidated via A30e-1). The version is part of
 * the effect-deps, so bumping it triggers a fresh fetch. Pre-blob-fetch
 * behavior appended ``?v={n}`` to the URL; that pattern doesn't survive
 * the auth fix below, but the same cache-bust semantics are preserved.
 *
 * P0-photo-401 (post-30j-arc dry-run, 2026-05-05): photo endpoint requires
 * ``Bearer`` JWT via ``Authorization`` header (``Depends(requires("member"))``
 * in ``backend/app/api/candidates.py``). Browsers do NOT add the
 * Authorization header on plain ``<img src>`` requests, so every photo
 * came back 401 once the dry-run user actually browsed candidate pages.
 *
 * Fix shape: fetch the photo bytes via ``apiClient`` (which adds the
 * Authorization header + handles 401-refresh-retry), turn the response
 * into an ``ObjectURL`` via ``URL.createObjectURL``, hand that blob URL
 * to ``<Avatar>`` as ``src``. The auth-touching path is now the same as
 * every other tenant-scoped API call. No backend surface change.
 *
 * Lifecycle: the previous blob URL is revoked on candidateId/version
 * change AND on unmount, so the browser never leaks blob handles.
 *
 * Finding #18 — console-debt 404 spam (walkthrough 2026-05-12): candidates
 * without an uploaded photo (no LinkedIn URL, unavatar fetch failed, or
 * 7-day failure-TTL active) make the photo endpoint 404. The visible
 * fallback (initials) is correct, but every mount of every avatar on
 * the Dashboard / Candidates table re-fetches and re-404s. On a dense
 * Dashboard with 25 ranked candidates that all 404, scrolling /
 * tab-switching / StrictMode-double-mount produces dozens of console
 * 404 entries per minute.
 *
 * Fix shape: module-level Map<string, "ok" | "missing"> keyed by
 * ``tenantSlug:candidateId:version`` caches the per-mount outcome.
 * On 404 (or any fetch error), we mark "missing" and short-circuit
 * subsequent mounts for the same key — they render initials directly
 * without firing the network request. On 200, we mark "ok"; we still
 * re-fetch on remount (the response is a Blob, not a URL we can stash
 * safely across mounts), but the network entry is a real photo, not
 * a 404. ``version`` is part of the key, so the A30e-3 cache-bust
 * semantic is preserved: bumping version invalidates the cached
 * "missing" verdict and triggers a fresh fetch.
 *
 * The cache is module-scoped (page-load lifetime). Hard-refresh /
 * navigation away clears it — which is correct: server-side cache may
 * have flipped from failed → ok in the meantime (admin manual refresh
 * via POST /photo/refresh, or 7-day TTL expiry triggering a re-fetch).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Avatar } from "@/components/common/Avatar";
import { apiClient } from "@/lib/apiClient";

type CandidatePhotoSize = "sm" | "md" | "lg" | "xl";

interface CandidatePhotoProps {
  candidateId: string;
  candidateName: string;
  size?: CandidatePhotoSize;
  /**
   * A30e-3 cache-buster. Bumping this re-fetches the photo (effect-dep).
   * Default 0 — no special behavior.
   */
  version?: number;
}

const SIZE_MAP: Record<CandidatePhotoSize, "sm" | "lg" | "xl" | "2xl"> = {
  sm: "sm",
  md: "lg",
  lg: "xl",
  xl: "2xl",
};

/**
 * Module-scoped per-(tenant, candidate, version) fetch outcome cache.
 *
 * "missing" → the photo endpoint 404'd (or otherwise failed) last time;
 *             skip the network request on subsequent mounts and render
 *             initials directly.
 * "ok"      → the photo endpoint returned bytes last time; we still
 *             re-fetch on remount, but the network entry is a real
 *             photo, not a 404.
 *
 * Exported for testing only — production code must NOT mutate this.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const _photoOutcomeCache = new Map<string, "ok" | "missing">();

function _cacheKey(tenantSlug: string, candidateId: string, version: number): string {
  return `${tenantSlug}:${candidateId}:v${version}`;
}

export function CandidatePhoto({
  candidateId,
  candidateName,
  size = "md",
  version = 0,
}: CandidatePhotoProps): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantSlug || !candidateId) {
      return;
    }

    // Finding #18: short-circuit when a previous mount for this
    // (tenant, candidate, version) already 404'd. Render initials
    // immediately; do not re-fire the request.
    const key = _cacheKey(tenantSlug, candidateId, version);
    if (_photoOutcomeCache.get(key) === "missing") {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;
    const url = `/api/t/${tenantSlug}/candidates/${candidateId}/photo`;

    apiClient
      .get<Blob>(url, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) {
          return;
        }
        _photoOutcomeCache.set(key, "ok");
        createdUrl = URL.createObjectURL(resp.data);
        setBlobUrl(createdUrl);
      })
      .catch(() => {
        // Avatar shows initials when src is null — no extra error UI needed.
        // Finding #18: remember this (tenant, candidate, version) 404'd
        // so we don't re-fire on subsequent mounts.
        _photoOutcomeCache.set(key, "missing");
        if (!cancelled) {
          setBlobUrl(null);
        }
      });

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [tenantSlug, candidateId, version]);

  return <Avatar src={blobUrl} name={candidateName} size={SIZE_MAP[size]} />;
}
